import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit2, Trash2, Briefcase, Lock, MessageCircle, Send, Video, CheckCircle2, X, GripVertical, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isManagerRole } from '../lib/roles';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { value: 'pitched', label: 'Pitched', color: '#0055FF', icon: Briefcase },
  { value: 'outreach', label: 'In Outreach', color: '#9B6EFF', icon: MessageCircle },
  { value: 'locked', label: 'Locked', color: '#00C972', icon: Lock },
  { value: 'live', label: 'Live', color: '#FF451A', icon: Video },
  { value: 'completed', label: 'Completed', color: '#666666', icon: CheckCircle2 },
  { value: 'declined', label: 'Declined', color: '#FF3B30', icon: X },
];

const stageMeta = (v) => STAGES.find((s) => s.value === v) || { label: v, color: '#999999', icon: Briefcase };

export const Campaigns = () => {
  const { user, creators, refreshCreators } = useAuth();
  const isManager = isManagerRole(user);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', brand: '', budget: '', deliverables: '', deadline: '', stage: 'pitched', notes: '', assigned_creator_id: '',
  });

  const fetchCampaigns = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/campaigns`, { withCredentials: true });
      setCampaigns(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchCampaigns();
  }, [user]);

  const fetchCreators = async () => {
    setCreatorsLoading(true);
    try { await refreshCreators(); } finally { setCreatorsLoading(false); }
  };

  const openCreateDialog = async () => { setShowCreateDialog(true); await fetchCreators(); };

  const resetForm = () => {
    setShowCreateDialog(false);
    setEditingCampaign(null);
    setFormData({ name: '', brand: '', budget: '', deliverables: '', deadline: '', stage: 'pitched', notes: '', assigned_creator_id: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, budget: parseFloat(formData.budget) };
      if (editingCampaign) {
        await axios.patch(`${API_URL}/api/campaigns/${editingCampaign.id}`, payload, { withCredentials: true });
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API_URL}/api/campaigns`, payload, { withCredentials: true });
        toast.success('Campaign created');
      }
      resetForm();
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save campaign');
    }
  };

  const handleEdit = async (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      brand: campaign.brand,
      budget: campaign.budget,
      deliverables: campaign.deliverables,
      deadline: campaign.deadline || '',
      stage: campaign.stage,
      notes: campaign.notes || '',
      assigned_creator_id: campaign.assigned_creator_id || '',
    });
    setShowCreateDialog(true);
    await fetchCreators();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API_URL}/api/campaigns/${id}`, { withCredentials: true });
      fetchCampaigns();
    } catch (e) {
      toast.error('Failed to delete campaign');
    }
  };

  const updateStage = async (id, nextStage) => {
    // optimistic
    setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, stage: nextStage } : c)));
    try {
      await axios.patch(`${API_URL}/api/campaigns/${id}`, { stage: nextStage }, { withCredentials: true });
      toast.success(`Moved to ${stageMeta(nextStage).label}`);
    } catch (e) {
      toast.error('Failed to update stage');
      fetchCampaigns();
    }
  };

  // Creator actions
  const accept = async (id) => {
    try {
      await axios.post(`${API_URL}/api/campaigns/${id}/accept`, {}, { withCredentials: true });
      toast.success('Campaign locked! 🎉');
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept');
    }
  };
  const decline = async (id) => {
    if (!window.confirm('Decline this campaign? This cannot be undone.')) return;
    try {
      await axios.post(`${API_URL}/api/campaigns/${id}/decline`, {}, { withCredentials: true });
      toast.success('Campaign declined');
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline');
    }
  };

  if (loading) {
    return (
      <div className="flex"><Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="campaigns-page">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2">Campaigns</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">
                {isManager ? 'Drag-style Kanban — click stage badges to move cards across pipeline.' : 'Your pitched & locked campaigns.'}
              </p>
            </div>
            {isManager && (
              <Button
                onClick={openCreateDialog}
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17] transition-colors focus:ring-2 focus:ring-[#FF451A]/30"
                data-testid="create-campaign-dialog-button"
              >
                <Plus size={18} className="mr-2" /> New Campaign
              </Button>
            )}
          </div>

          {campaigns.length === 0 ? (
            <EmptyState isManager={isManager} />
          ) : isManager ? (
            <div className="-mx-2 px-2">
              <KanbanBoard
                campaigns={campaigns}
                onMove={updateStage}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </div>
          ) : (
            <CreatorView
              campaigns={campaigns}
              onAccept={accept}
              onDecline={decline}
            />
          )}

          <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}</DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] text-[#0F0F0F] dark:text-white">Assign to Creator</label>
                    <button type="button" onClick={fetchCreators} className="text-xs text-[#FF451A] hover:text-[#E63E17] font-medium" data-testid="refresh-creators-button">
                      {creatorsLoading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                  <Select value={formData.assigned_creator_id} onValueChange={(v) => setFormData({ ...formData, assigned_creator_id: v })} required>
                    <SelectTrigger className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-creator-select">
                      <SelectValue placeholder={`Select a creator (${creators.length} available)`} />
                    </SelectTrigger>
                    <SelectContent>
                      {creatorsLoading ? <div className="px-4 py-2 text-sm">Loading...</div> : creators.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-[#6B6B6B] dark:text-zinc-500">No creators registered yet.</div>
                      ) : [...creators].sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())).map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Campaign Name</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Summer Fashion Campaign" className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Brand Name</label>
                  <Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} required placeholder="Nike, Maybelline..." className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-brand-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Budget (₹)</label>
                    <Input type="number" value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} required placeholder="50000" className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-budget-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Stage</label>
                    <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                      <SelectTrigger className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-stage-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Deliverables</label>
                  <textarea value={formData.deliverables} onChange={(e) => setFormData({ ...formData, deliverables: e.target.value })} required placeholder="3 Instagram Reels, 5 Stories" className="w-full rounded-xl px-4 py-3 min-h-[80px] bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-deliverables-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Deadline</label>
                  <Input type="date" value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} className="rounded-xl px-4 py-3 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-deadline-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes for the creator..." className="w-full rounded-xl px-4 py-3 min-h-[80px] bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="campaign-notes-input" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1 bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#E63E17] transition-colors" data-testid="campaign-submit-button">
                    {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                  </Button>
                  <Button type="button" onClick={resetForm} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6 py-2.5 font-medium" data-testid="campaign-cancel-button">Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

// --- Manager Kanban Board ---
const KanbanBoard = ({ campaigns, onMove, onEdit, onDelete }) => {
  const columns = STAGES.map((s) => ({
    ...s,
    items: campaigns.filter((c) => c.stage === s.value),
  }));

  return (
    <div className="overflow-x-auto pb-4" data-testid="kanban-board">
      <div className="flex gap-4 min-w-max">
        {columns.map((col) => {
          const Icon = col.icon;
          return (
            <div key={col.value} className="w-60 shrink-0">
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${col.color}15` }}>
                  <Icon size={12} style={{ color: col.color }} />
                </div>
                <h3 className="font-semibold text-sm text-[#0F0F0F] dark:text-white truncate">{col.label}</h3>
                <span className="ml-auto text-xs text-[#6B6B6B] dark:text-zinc-500 font-medium">{col.items.length}</span>
              </div>
              <div className="space-y-3 min-h-[60vh] bg-black/[0.02] rounded-2xl p-3" data-testid={`kanban-column-${col.value}`}>
                {col.items.length === 0 && (
                  <div className="text-center py-8 text-xs text-[#6B6B6B] dark:text-zinc-500">No cards</div>
                )}
                {col.items.map((c) => (
                  <KanbanCard
                    key={c.id}
                    campaign={c}
                    onMove={onMove}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const KanbanCard = ({ campaign, onMove, onEdit, onDelete }) => {
  const [stageOpen, setStageOpen] = useState(false);
  const meta = stageMeta(campaign.stage);
  return (
    <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4 shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:-translate-y-[1px] transition-all" data-testid="campaign-card">
      <div className="flex items-start gap-2 mb-2">
        <GripVertical size={14} className="text-[#CCCCCC] mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <Link to={`/campaigns/${campaign.id}/workspace`} className="block">
            <h4 className="font-semibold text-sm text-[#0F0F0F] dark:text-white line-clamp-2 hover:text-[#FF451A] transition-colors">{campaign.name}</h4>
          </Link>
          <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-0.5 truncate">{campaign.brand}</p>
        </div>
      </div>

      <div className="space-y-1.5 text-xs mb-3">
        <div className="flex justify-between"><span className="text-[#6B6B6B] dark:text-zinc-500">Budget</span><span className="font-medium text-[#0F0F0F] dark:text-white">₹{(campaign.budget || 0).toLocaleString()}</span></div>
        {campaign.assigned_creator_name && (
          <div className="flex justify-between"><span className="text-[#6B6B6B] dark:text-zinc-500">Creator</span><span className="font-medium text-[#0F0F0F] dark:text-white truncate ml-2">{campaign.assigned_creator_name}</span></div>
        )}
        {campaign.deadline && (
          <div className="flex justify-between"><span className="text-[#6B6B6B] dark:text-zinc-500">Deadline</span><span className="font-medium text-[#0F0F0F] dark:text-white">{new Date(campaign.deadline).toLocaleDateString()}</span></div>
        )}
      </div>

      <div className="flex gap-1 items-center">
        <div className="relative flex-1">
          <button
            onClick={() => setStageOpen((v) => !v)}
            className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
            data-testid="campaign-stage-button"
          >
            <meta.icon size={11} /> {meta.label}
          </button>
          {stageOpen && (
            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 rounded-xl shadow-lg z-20 overflow-hidden">
              {STAGES.filter((s) => s.value !== campaign.stage).map((s) => (
                <button
                  key={s.value}
                  onClick={() => { onMove(campaign.id, s.value); setStageOpen(false); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2"
                  style={{ color: s.color }}
                  data-testid={`stage-option-${s.value}`}
                >
                  <s.icon size={11} /> {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => onEdit(campaign)} className="p-1.5 rounded-md text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] hover:bg-white/5" data-testid="campaign-edit-button"><Edit2 size={13} /></button>
        <button onClick={() => onDelete(campaign.id)} className="p-1.5 rounded-md text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF3B30] hover:bg-white/5" data-testid="campaign-delete-button"><Trash2 size={13} /></button>
      </div>
    </div>
  );
};

// --- Creator Pitched / Locked View with Accept / Decline ---
const CreatorView = ({ campaigns, onAccept, onDecline }) => {
  const pitched = campaigns.filter((c) => c.stage === 'pitched');
  const inProgress = campaigns.filter((c) => ['outreach', 'locked', 'live'].includes(c.stage));
  const archived = campaigns.filter((c) => ['completed', 'declined'].includes(c.stage));

  return (
    <div className="space-y-10">
      <Section title="📬 Pitched (Action Required)" color="#0055FF" count={pitched.length} empty="No pitched campaigns right now. Sit tight — brands are scouting!">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pitched.map((c) => (
            <CreatorCard key={c.id} campaign={c} onAccept={() => onAccept(c.id)} onDecline={() => onDecline(c.id)} actionable />
          ))}
        </div>
      </Section>

      <Section title="🚀 In Progress" color="#00C972" count={inProgress.length} empty="No active deals yet.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {inProgress.map((c) => <CreatorCard key={c.id} campaign={c} />)}
        </div>
      </Section>

      {archived.length > 0 && (
        <Section title="📦 Archived" color="#999999" count={archived.length}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {archived.map((c) => <CreatorCard key={c.id} campaign={c} />)}
          </div>
        </Section>
      )}
    </div>
  );
};

const Section = ({ title, count, color, children, empty }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <h2 className="text-lg font-semibold text-[#0F0F0F] dark:text-white">{title}</h2>
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${color}15`, color }}>{count}</span>
    </div>
    {count === 0 ? (
      empty ? <p className="text-sm text-[#6B6B6B] dark:text-zinc-500 px-1">{empty}</p> : null
    ) : children}
  </div>
);

const CreatorCard = ({ campaign, onAccept, onDecline, actionable }) => {
  const meta = stageMeta(campaign.stage);
  const Icon = meta.icon;
  return (
    <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-5 hover:-translate-y-[1px] transition-transform" data-testid="campaign-card">
      <div className="flex items-center justify-between mb-3">
        <span className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
          <Icon size={11} /> {meta.label}
        </span>
        <span className="text-xs text-[#0F0F0F] dark:text-white font-semibold">₹{(campaign.budget || 0).toLocaleString()}</span>
      </div>
      <Link to={`/campaigns/${campaign.id}/workspace`} className="block">
        <h3 className="font-semibold text-[#0F0F0F] dark:text-white mb-1 hover:text-[#FF451A] transition-colors">{campaign.name}</h3>
      </Link>
      <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-3">{campaign.brand}</p>
      {campaign.deliverables && (
        <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg px-3 py-2 mb-3">
          <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-0.5">Deliverables</div>
          {campaign.deliverables}
        </div>
      )}
      {campaign.deadline && (
        <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mb-3">Deadline: <span className="font-medium text-[#0F0F0F] dark:text-white">{new Date(campaign.deadline).toLocaleDateString()}</span></p>
      )}
      {campaign.notes && (
        <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mb-3 line-clamp-3 italic">"{campaign.notes}"</p>
      )}
      {actionable && (
        <div className="flex gap-2 pt-2 border-t border-black/[0.08] dark:border-white/5">
          <Button
            onClick={onAccept}
            className="flex-1 bg-[#00C972] hover:bg-[#00b362] text-[#0F0F0F] dark:text-white rounded-full text-sm py-2 font-medium"
            data-testid="accept-campaign-button"
          >
            <Check size={14} className="mr-1" /> Accept
          </Button>
          <Button
            onClick={onDecline}
            className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#FF3B30] hover:bg-[#FF3B30]/5 rounded-full text-sm py-2 font-medium"
            data-testid="decline-campaign-button"
          >
            <X size={14} className="mr-1" /> Decline
          </Button>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ isManager }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-12 text-center">
    <Send size={40} className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" />
    <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">No campaigns yet</h3>
    <p className="text-[#6B6B6B] dark:text-zinc-400 mb-6">
      {isManager ? 'Create your first campaign or wait for admin to assign brand briefs.' : "You haven't been pitched to any campaigns yet."}
    </p>
  </div>
);
